import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, Shield, Info, List } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const SecuredAssetsCoreConceptPage = () => {
    const [loading, setLoading] = useState(true);

    // Core Settings State
    const [coreSettings, setCoreSettings] = useState({
        section_tag: 'Core Concept',
        section_title: 'Understanding Secured Assets in Home & Housing Finance',
        definition_text: '',
        mechanism_heading: '',
        protection_note: '',
        is_active: true
    });

    // Core Benefits State
    const [coreBenefits, setCoreBenefits] = useState([]);
    const [isBenefitModalOpen, setIsBenefitModalOpen] = useState(false);
    const [benefitModalData, setBenefitModalData] = useState({
        icon_name: '',
        title: '',
        description: '',
        display_order: 0,
        is_active: true
    });
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [benefitDeleteConfirm, setBenefitDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsResponse, benefitsResponse] = await Promise.all([
                axios.get('/api/admin/secured-assets/core-settings'),
                axios.get('/api/admin/secured-assets/core-benefits')
            ]);

            if (settingsResponse.data.success && settingsResponse.data.data) {
                setCoreSettings(settingsResponse.data.data);
            }
            if (benefitsResponse.data.success) {
                setCoreBenefits(benefitsResponse.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCoreSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCoreSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleCoreSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/secured-assets/core-settings', coreSettings);
            toast.success('Core settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddBenefit = () => {
        setEditingBenefit(null);
        setBenefitModalData({
            icon_name: 'Shield',
            title: '',
            description: '',
            display_order: coreBenefits.length + 1,
            is_active: true
        });
        setIsBenefitModalOpen(true);
    };

    const handleEditBenefit = (benefit) => {
        setEditingBenefit(benefit);
        setBenefitModalData({
            ...benefit,
            is_active: Boolean(benefit.is_active)
        });
        setIsBenefitModalOpen(true);
    };

    const handleStatusToggle = async (benefit) => {
        try {
            const updatedBenefit = { ...benefit, is_active: !benefit.is_active };
            await axios.put(`/api/admin/secured-assets/core-benefits/${benefit.id}`, updatedBenefit);
            toast.success('Benefit status updated');
            fetchData();
        } catch (error) {
            console.error('Error updating benefit status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleBenefitSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingBenefit) {
                await axios.put(`/api/admin/secured-assets/core-benefits/${editingBenefit.id}`, benefitModalData);
                toast.success('Benefit updated');
            } else {
                await axios.post('/api/admin/secured-assets/core-benefits', benefitModalData);
                toast.success('Benefit created');
            }
            setIsBenefitModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving benefit:', error);
            toast.error('Failed to save benefit');
        }
    };

    const confirmDeleteBenefit = async () => {
        try {
            await axios.delete(`/api/admin/secured-assets/core-benefits/${benefitDeleteConfirm.id}`);
            toast.success('Benefit deleted');
            setBenefitDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting benefit:', error);
            toast.error('Failed to delete benefit');
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Secured Assets Core Concept</h1>
                    <p className="text-slate-500 mt-1">Manage the definition and benefits section of the Secured Assets page</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex items-center gap-2 mb-6 text-primary-900">
                    <Info size={20} className="font-bold" />
                    <h2 className="text-lg font-bold">Section Header & Definition</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Tag</label>
                        <input
                            type="text"
                            name="section_tag"
                            value={coreSettings.section_tag || ''}
                            onChange={handleCoreSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="Core Concept"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={coreSettings.section_title || ''}
                            onChange={handleCoreSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="Understanding Secured Assets..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Definition Text</label>
                        <textarea
                            name="definition_text"
                            value={coreSettings.definition_text || ''}
                            onChange={handleCoreSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-32 resize-none"
                            placeholder="Describe what a secured asset is..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Mechanism Heading</label>
                        <input
                            type="text"
                            name="mechanism_heading"
                            value={coreSettings.mechanism_heading || ''}
                            onChange={handleCoreSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="This mechanism ensures that..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Bottom Protection Note</label>
                        <textarea
                            name="protection_note"
                            value={coreSettings.protection_note || ''}
                            onChange={handleCoreSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                            placeholder="Final note about protecting interests..."
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleCoreSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Benefits Management */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2 text-primary-900">
                        <List size={20} className="font-bold" />
                        <h2 className="text-xl font-bold">Benefit Cards</h2>
                    </div>
                    <button
                        onClick={handleAddBenefit}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Benefit
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {coreBenefits.map((benefit) => (
                        <div key={benefit.id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 relative group overflow-hidden">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600">
                                    <Shield size={24} />
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleEditBenefit(benefit)}
                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        onClick={() => setBenefitDeleteConfirm({ isOpen: true, id: benefit.id })}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <h3 className="font-bold text-slate-800 mb-2">{benefit.title}</h3>
                            <p className="text-sm text-slate-500 leading-relaxed mb-4">{benefit.description}</p>
                            <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                                <span className="text-xs font-medium text-slate-400">Order: {benefit.display_order}</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={Boolean(benefit.is_active)}
                                        onChange={() => handleStatusToggle(benefit)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                                </label>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Benefit Modal */}
            {isBenefitModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingBenefit ? 'Edit Benefit' : 'Add New Benefit'}</h2>
                            <button onClick={() => setIsBenefitModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleBenefitSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Icon (Lucide Name)</label>
                                    <input
                                        type="text"
                                        value={benefitModalData.icon_name}
                                        onChange={(e) => setBenefitModalData({ ...benefitModalData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="e.g. Shield, User"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={benefitModalData.display_order}
                                        onChange={(e) => setBenefitModalData({ ...benefitModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        min="1"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={benefitModalData.title}
                                    onChange={(e) => setBenefitModalData({ ...benefitModalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    placeholder="Benefit Title"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={benefitModalData.description}
                                    onChange={(e) => setBenefitModalData({ ...benefitModalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    placeholder="Describe the benefit..."
                                    required
                                />
                            </div>
                            <div className="flex flex-col">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={benefitModalData.is_active}
                                        onChange={(e) => setBenefitModalData({ ...benefitModalData, is_active: e.target.checked })}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                    <span className="ml-3 text-sm font-medium text-slate-700">{benefitModalData.is_active ? 'Active' : 'Inactive'}</span>
                                </label>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsBenefitModalOpen(false)}
                                    className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg"
                                >
                                    {editingBenefit ? 'Update Benefit' : 'Create Benefit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={benefitDeleteConfirm.isOpen}
                onClose={() => setBenefitDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteBenefit}
                title="Delete Benefit"
                message="Are you sure you want to delete this benefit? This action cannot be undone."
            />
        </div>
    );
};

export default SecuredAssetsCoreConceptPage;
